import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { shallowEqual } from "react-redux";
import { FormErrorMessage } from "..";
import { ApiSchemaVersionConfig } from "../../models/api";
import { useFunctionalityVersionConfig } from "../../pages/hooks/functionalityChangelog";
import { useAppSelector, VRIAppStateType } from "../../core";
import { compareAppVersions, isValidAppVersion } from "../../utils";

type Props = {
  show: boolean;
  handleShow: (value: boolean) => void;
};

export function ModalVersionConfig(props: Props): JSX.Element {
  const { show, handleShow } = props;
  const { getVersionConfig, onUpdateVersionConfig } = useFunctionalityVersionConfig();
  const [isLoading, setIsLoading] = useState(false);
  const [currentConfig, setCurrentConfig] = useState<ApiSchemaVersionConfig | null>(null);

  const adminName = useAppSelector(
    (state: VRIAppStateType) => state.auth.profile?.name,
    shallowEqual
  );

  const { register, handleSubmit, formState: { errors }, reset, getValues } = useForm<ApiSchemaVersionConfig>({
    mode: "onBlur",
    defaultValues: {
      latestVersion: "",
      minimumVersion: "",
    },
    criteriaMode: "all",
  });

  useEffect(() => {
    if (!show) {
      return;
    }
    setIsLoading(true);
    getVersionConfig()
      .then((config) => {
        setCurrentConfig(config);
        if (config) {
          reset({
            latestVersion: config.latestVersion || "",
            minimumVersion: config.minimumVersion || "",
          });
        }
      })
      .finally(() => setIsLoading(false));
  }, [show, getVersionConfig, reset]);

  const modalHideHandler = () => {
    handleShow(false);
    reset();
  };

  function isValidVersionFormat(input: string) {
    return isValidAppVersion(input) || "Version must be in proper numeric format, example: 1.2.3";
  }

  function isMinimumNotAboveLatest(minimum: string) {
    const latest = getValues("latestVersion");
    if (!isValidAppVersion(minimum) || !isValidAppVersion(latest)) {
      return true;
    }
    return (
      compareAppVersions(minimum, latest) <= 0 ||
      "Minimum version cannot be higher than latest version"
    );
  }

  const onSubmitHandler = async (form: ApiSchemaVersionConfig) => {
    const succeeded = await onUpdateVersionConfig({
      latestVersion: form.latestVersion,
      minimumVersion: form.minimumVersion,
      updatedBy: adminName || "",
    });
    if (succeeded) {
      modalHideHandler();
    }
  };

  return (
    <Modal show={show} onHide={modalHideHandler} centered id="versionConfigModal">
      <Modal.Header closeButton>
        <Modal.Title>App Version Config</Modal.Title>
      </Modal.Header>

      <form onSubmit={handleSubmit(onSubmitHandler)}>
        <Modal.Body>
          {isLoading ? (
            <div className="text-center py-3">Loading...</div>
          ) : (
            <>
              <div className="row">
                <div className="col-12">
                  <div className="form-group">
                    <label htmlFor="latestVersion" className="position-relative required">
                      Latest Version
                    </label>
                    <input
                      type="text"
                      {...register("latestVersion", {
                        required: "Latest version is required",
                        validate: isValidVersionFormat,
                      })}
                      id="latestVersion"
                      className="form-control"
                      placeholder="1.2.3"
                    />
                    <small className="form-text text-muted">
                      Users on an older version see a "new version available" notice. Set this only after the new build is deployed.
                    </small>
                    {errors.latestVersion && <FormErrorMessage message={errors.latestVersion.message || ""} />}
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-12">
                  <div className="form-group">
                    <label htmlFor="minimumVersion" className="position-relative required">
                      Minimum Version
                    </label>
                    <input
                      type="text"
                      {...register("minimumVersion", {
                        required: "Minimum version is required",
                        validate: {
                          format: isValidVersionFormat,
                          notAboveLatest: isMinimumNotAboveLatest,
                        },
                      })}
                      id="minimumVersion"
                      className="form-control"
                      placeholder="1.2.3"
                    />
                    <small className="form-text text-muted">
                      Users below this version are blocked from logging in and making calls until they update. Raise it only for breaking releases.
                    </small>
                    {errors.minimumVersion && <FormErrorMessage message={errors.minimumVersion.message || ""} />}
                  </div>
                </div>
              </div>

              {currentConfig?.updatedAt ? (
                <div className="row">
                  <div className="col-12">
                    <small className="text-muted">
                      Last updated {new Date(currentConfig.updatedAt).toLocaleString()}
                      {currentConfig.updatedBy ? ` by ${currentConfig.updatedBy}` : ""}
                    </small>
                  </div>
                </div>
              ) : (
                <></>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="btn btn-link text-site text-uppercase"
            onClick={modalHideHandler}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary text-uppercase version-config-submit-btn"
            disabled={isLoading}
          >
            Save
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
